import { contactTypeRoutes } from '@modules/Contacts/Resources/js/contactRoutes';
import { listObjectTypeForDocType } from '@/config/documentTypes';

/**
 * Accounting module — Sales, Purchases, Banking, Payments, Expenses, Tax Reports
 *
 * Helper to generate the repetitive list/preview/new/edit routes
 * for each accounting document type.
 */
function accountingRoutes(section, type, names, options = {}) {
    const listKey = listObjectTypeForDocType(`${section}/${type}`);

    return [
        // List + Preview
        {
            path: `${section}/${type}`,
            name: names.list,
            component: () => import('@modules/Accounting/Resources/js/pages/documents/List.vue'),
            props: { docType: `${section}/${type}` },
            // What the list asks for as it opens, in one request (utils/listBootstrap.js) —
            // only for the document types that have a list of their own.
            ...(listKey ? { meta: { listBootstrap: { list: listKey } } } : {}),
            children: [
                {
                    path: ':id(\\d+)',
                    name: names.preview,
                    component: () => import('@modules/Accounting/Resources/js/pages/documents/Preview.vue'),
                    props: true,
                    redirect: { name: names.overview },
                    children: [
                        {
                            path: 'overview',
                            name: names.overview,
                            component: () =>
                                import('@modules/Accounting/Resources/js/pages/documents/preview/Overview.vue'),
                            props: true
                        },
                        {
                            path: 'comments',
                            name: names.comments || `${names.list}-comments`,
                            component: () =>
                                import('@modules/Accounting/Resources/js/pages/documents/preview/Comments.vue'),
                            props: true
                        },
                        {
                            path: 'documents/:docPath(.*)*',
                            name: names.documents || `${names.list}-documents`,
                            component: () => import('@/components/base/tabs/TabDocuments.vue'),
                            props: true
                        },
                        {
                            path: 'related',
                            name: names.related || `${names.list}-related`,
                            component: () =>
                                import('@modules/Accounting/Resources/js/pages/documents/preview/RelatedDocuments.vue'),
                            props: true
                        },
                        {
                            path: 'versions',
                            name: names.versions || names.preview.replace('-preview', '-versions'),
                            component: () =>
                                import('@modules/Accounting/Resources/js/pages/documents/preview/Versions.vue'),
                            props: true
                        },
                        {
                            // The ledger's audit trail for this document: what
                            // it holds now, and what was posted or discarded to
                            // get there.
                            path: 'ledger',
                            name: names.ledger || names.preview.replace('-preview', '-ledger'),
                            component: () =>
                                import('@modules/Accounting/Resources/js/pages/documents/preview/Ledger.vue'),
                            props: true
                        },
                        {
                            path: 'logs',
                            name: names.logs || names.preview.replace('-preview', '-logs'),
                            component: () => import('@/components/base/tabs/TabLogs.vue'),
                            props: true
                        },
                        ...(options.extraChildren || [])
                    ]
                }
            ]
        },
        // New
        {
            path: `${section}/${type}/new`,
            name: names.new,
            component: () => import('@modules/Accounting/Resources/js/pages/documents/Form.vue'),
            props: { docType: `${section}/${type}` }
        },
        // Edit
        {
            path: `${section}/${type}/:id(\\d+)/edit`,
            name: names.edit,
            component: () => import('@modules/Accounting/Resources/js/pages/documents/Form.vue'),
            props: route => ({ docType: `${section}/${type}`, id: route.params.id })
        },
        // Send Email
        ...(names.sendEmail
            ? [
                  {
                      path: `${section}/${type}/:id(\\d+)/send-email`,
                      name: names.sendEmail,
                      component: () => import('@modules/Accounting/Resources/js/pages/documents/SendEmail.vue'),
                      props: route => ({ docType: `${section}/${type}`, id: route.params.id })
                  }
              ]
            : [])
    ];
}

/**
 * Helper to generate payment routes (customer-payments, supplier-payments)
 * pointing to shared components in pages/payments/.
 */
function paymentRoutes(path, type, names) {
    return [
        {
            path,
            name: names.list,
            component: () => import('@modules/Accounting/Resources/js/pages/payments/List.vue'),
            props: { type },
            children: [
                {
                    path: ':id(\\d+)',
                    name: names.preview,
                    component: () => import('@modules/Accounting/Resources/js/pages/payments/Preview.vue'),
                    redirect: { name: names.overview },
                    props: route => ({ id: route.params.id, type }),
                    children: [
                        {
                            path: `/organization/:org/${path}/:id(\\d+)`,
                            name: names.overview,
                            component: () =>
                                import('@modules/Accounting/Resources/js/pages/payments/preview/Overview.vue'),
                            props: true,
                            meta: { paymentType: type }
                        }
                    ]
                }
            ]
        },
        {
            path: `${path}/new`,
            name: names.new,
            component: () => import('@modules/Accounting/Resources/js/pages/payments/Form.vue'),
            props: { defaultType: type }
        },
        {
            path: `${path}/:id(\\d+)/edit`,
            name: names.edit,
            component: () => import('@modules/Accounting/Resources/js/pages/payments/Form.vue'),
            props: route => ({ defaultType: type, id: route.params.id })
        }
    ];
}

// The customer list and its screens now live in Contacts, whose record and
// ability they always were: an organization that bought no part of Accounting
// used to lose them, and they were never Accounting's to sell.
const salesRoutes = [
    ...accountingRoutes('sales', 'quotes', {
        list: 'sales-quotes',
        preview: 'sales-quote-preview',
        overview: 'sales-quote-overview',
        comments: 'sales-quote-comments',
        new: 'sales-quote-new',
        edit: 'sales-quote-edit',
        sendEmail: 'sales-quote-send-email'
    }),
    ...accountingRoutes(
        'sales',
        'orders',
        {
            list: 'sales-orders',
            preview: 'sales-order-preview',
            overview: 'sales-order-overview',
            comments: 'sales-order-comments',
            documents: 'sales-orders-documents',
            new: 'sales-order-new',
            edit: 'sales-order-edit',
            sendEmail: 'sales-order-send-email'
        },
        {
            extraChildren: [
                {
                    path: 'shipments',
                    name: 'sales-order-shipments',
                    component: () => import('@modules/Accounting/Resources/js/pages/documents/preview/Shipments.vue'),
                    props: true
                }
            ]
        }
    ),
    ...accountingRoutes('sales', 'invoices', {
        list: 'sales-invoices',
        preview: 'sales-invoice-preview',
        overview: 'sales-invoice-overview',
        comments: 'sales-invoice-comments',
        new: 'sales-invoice-new',
        edit: 'sales-invoice-edit',
        sendEmail: 'sales-invoice-send-email'
    }),
    ...accountingRoutes('sales', 'credit-notes', {
        list: 'sales-credit-notes',
        preview: 'sales-credit-note-preview',
        overview: 'sales-credit-note-overview',
        comments: 'sales-credit-note-comments',
        documents: 'sales-credit-notes-documents',
        new: 'sales-credit-note-new',
        edit: 'sales-credit-note-edit',
        sendEmail: 'sales-credit-note-send-email'
    })
];

const purchasesRoutes = [
    contactTypeRoutes('supplier', 'suppliers', 'suppliers-list'),
    ...accountingRoutes(
        'purchases',
        'orders',
        {
            list: 'purchases-orders',
            preview: 'purchases-order-preview',
            overview: 'purchases-order-overview',
            comments: 'purchases-order-comments',
            documents: 'purchases-orders-documents',
            new: 'purchases-order-new',
            edit: 'purchases-order-edit',
            sendEmail: 'purchases-order-send-email'
        },
        {
            extraChildren: [
                {
                    path: 'receipts',
                    name: 'purchases-order-receipts',
                    component: () => import('@modules/Accounting/Resources/js/pages/documents/preview/Receipts.vue'),
                    props: true
                }
            ]
        }
    ),
    ...accountingRoutes('purchases', 'invoices', {
        list: 'purchases-invoices',
        preview: 'purchases-invoice-preview',
        overview: 'purchases-invoice-overview',
        comments: 'purchases-invoice-comments',
        new: 'purchases-invoice-new',
        edit: 'purchases-invoice-edit',
        sendEmail: 'purchases-invoice-send-email'
    }),
    ...accountingRoutes('purchases', 'credit-notes', {
        list: 'purchases-credit-notes',
        preview: 'purchases-credit-note-preview',
        overview: 'purchases-credit-note-overview',
        comments: 'purchases-credit-note-comments',
        documents: 'purchases-credit-notes-documents',
        new: 'purchases-credit-note-new',
        edit: 'purchases-credit-note-edit',
        sendEmail: 'purchases-credit-note-send-email'
    })
];

export default {
    slug: 'accounting',
    ability: 'accounting',
    contactTypes: [
        // `customer` is declared by Contacts: it is held whether or not any of
        // this module was bought. The supplier facet stays here — buying is what
        // this module is.
        {
            slug: 'supplier',
            label: () => i18n.t('Suppliers'),
            singularLabel: () => i18n.t('Supplier'),
            listRoute: 'suppliers-list',
            previewRoute: 'supplier-preview',
            routePrefix: 'supplier'
        }
    ],
    routes: [
        ...salesRoutes,
        ...purchasesRoutes,

        // Recurrences: one list for both directions, so it hangs off neither
        // `sales/` nor `purchases/` and does not go through accountingRoutes()
        // (no versions tab, no send-email, its own preview).
        {
            path: 'recurring-schedules',
            name: 'recurring-schedules',
            component: () => import('@modules/Accounting/Resources/js/pages/recurring/List.vue'),
            // What the list asks for as it opens, in one request (utils/listBootstrap.js).
            meta: { listBootstrap: { list: 'recurring_schedules' } },
            children: [
                {
                    path: ':id(\\d+)',
                    name: 'recurring-schedule-preview',
                    component: () => import('@modules/Accounting/Resources/js/pages/recurring/Preview.vue'),
                    redirect: { name: 'recurring-schedule-overview' },
                    props: true,
                    children: [
                        {
                            path: '/organization/:org/recurring-schedules/:id(\\d+)',
                            name: 'recurring-schedule-overview',
                            component: () =>
                                import('@modules/Accounting/Resources/js/pages/recurring/preview/Overview.vue'),
                            props: true
                        },
                        {
                            path: 'generated',
                            name: 'recurring-schedule-generated',
                            component: () =>
                                import('@modules/Accounting/Resources/js/pages/recurring/preview/GeneratedDocuments.vue'),
                            props: true
                        },
                        {
                            path: 'comments',
                            name: 'recurring-schedule-comments',
                            component: () => import('@/components/base/tabs/TabComments.vue'),
                            props: route => ({ id: route.params.id, entityType: 'recurring_schedule' })
                        },
                        {
                            path: 'documents/:docPath(.*)*',
                            name: 'recurring-schedule-documents',
                            component: () => import('@/components/base/tabs/TabDocuments.vue'),
                            props: route => ({ id: route.params.id, entityType: 'recurring_schedule' })
                        },
                        {
                            path: 'logs',
                            name: 'recurring-schedule-logs',
                            component: () => import('@/components/base/tabs/TabLogs.vue'),
                            props: route => ({ id: route.params.id, entityType: 'recurring_schedule' })
                        }
                    ]
                }
            ]
        },

        // Bookmarks pointing at the two lists this one replaces. A flat filter
        // param, not `q` — that one carries the advanced query builder's JSON
        // tree and rejects anything else as unreadable.
        {
            path: 'sales/recurring',
            redirect: { name: 'recurring-schedules', query: { direction: 'sales' } }
        },
        {
            path: 'purchases/recurring',
            redirect: { name: 'recurring-schedules', query: { direction: 'purchases' } }
        },

        {
            path: 'expenses',
            name: 'expenses-list',
            component: () => import('@modules/Accounting/Resources/js/pages/expenses/List.vue'),
            children: [
                {
                    path: ':id(\\d+)',
                    name: 'expenses-preview',
                    component: () => import('@modules/Accounting/Resources/js/pages/expenses/Preview.vue'),
                    redirect: { name: 'expenses-overview' },
                    props: true,
                    children: [
                        {
                            path: '/organization/:org/expenses/:id(\\d+)',
                            name: 'expenses-overview',
                            component: () =>
                                import('@modules/Accounting/Resources/js/pages/expenses/preview/Overview.vue'),
                            props: true
                        },
                        {
                            path: 'documents/:docPath(.*)*',
                            name: 'expenses-documents',
                            component: () => import('@/components/base/tabs/TabDocuments.vue'),
                            props: true
                        }
                    ]
                }
            ]
        },
        {
            path: 'tax-reports',
            name: 'tax-reports-list',
            component: () => import('@modules/Accounting/Resources/js/pages/tax-reports/List.vue'),
            children: [
                {
                    path: ':id(\\d+)',
                    name: 'tax-reports-preview',
                    component: () => import('@modules/Accounting/Resources/js/pages/tax-reports/Preview.vue'),
                    redirect: { name: 'tax-reports-overview' },
                    props: true,
                    children: [
                        {
                            path: '/organization/:org/tax-reports/:id(\\d+)',
                            name: 'tax-reports-overview',
                            component: () =>
                                import('@modules/Accounting/Resources/js/pages/tax-reports/preview/Overview.vue'),
                            props: true
                        }
                    ]
                }
            ]
        },

        {
            path: 'journal-entries',
            name: 'journal-entries-list',
            component: () => import('@modules/Accounting/Resources/js/pages/journal-entries/List.vue'),
            children: [
                {
                    path: ':id(\\d+)',
                    name: 'journal-entry-preview',
                    component: () => import('@modules/Accounting/Resources/js/pages/journal-entries/Preview.vue'),
                    redirect: { name: 'journal-entry-overview' },
                    props: true,
                    children: [
                        {
                            path: 'overview',
                            name: 'journal-entry-overview',
                            component: () =>
                                import('@modules/Accounting/Resources/js/pages/journal-entries/preview/Overview.vue'),
                            props: true
                        }
                    ]
                }
            ]
        },
        {
            path: 'journal-entries/new',
            name: 'journal-entry-new',
            component: () => import('@modules/Accounting/Resources/js/pages/journal-entries/Form.vue')
        },
        {
            path: 'journal-entries/:id(\\d+)/edit',
            name: 'journal-entry-edit',
            component: () => import('@modules/Accounting/Resources/js/pages/journal-entries/Form.vue'),
            props: route => ({ id: route.params.id })
        },
        {
            path: 'banking',
            name: 'banking-accounts',
            component: () => import('@modules/Accounting/Resources/js/pages/banking/AccountList.vue')
        },
        {
            path: 'banking/:accountId(\\d+)',
            name: 'banking-feed',
            component: () => import('@modules/Accounting/Resources/js/pages/banking/accounts/List.vue'),
            props: true,
            children: [
                {
                    path: ':id(\\d+)',
                    name: 'banking-transaction',
                    component: () => import('@modules/Accounting/Resources/js/pages/banking/accounts/Preview.vue'),
                    props: true
                }
            ]
        },
        {
            path: 'banking/:accountId(\\d+)/import',
            name: 'banking-import',
            component: () => import('@modules/Accounting/Resources/js/pages/banking/Import.vue'),
            props: true
        },
        {
            path: 'reports/profit-and-loss',
            name: 'report-profit-and-loss',
            component: () => import('@modules/Accounting/Resources/js/pages/reports/ProfitAndLoss.vue')
        },
        {
            path: 'reports/trial-balance',
            name: 'report-trial-balance',
            component: () => import('@modules/Accounting/Resources/js/pages/reports/TrialBalance.vue')
        },
        {
            path: 'reports/balance-sheet',
            name: 'report-balance-sheet',
            component: () => import('@modules/Accounting/Resources/js/pages/reports/BalanceSheet.vue')
        },
        {
            path: 'reports/general-ledger',
            name: 'report-general-ledger',
            component: () => import('@modules/Accounting/Resources/js/pages/reports/GeneralLedger.vue')
        },
        {
            path: 'reports/receivable-aging',
            name: 'report-receivable-aging',
            component: () => import('@modules/Accounting/Resources/js/pages/reports/ReceivableAging.vue')
        },
        {
            path: 'reports/payable-aging',
            name: 'report-payable-aging',
            component: () => import('@modules/Accounting/Resources/js/pages/reports/PayableAging.vue')
        },
        {
            path: 'reports/recurring-invoices',
            name: 'report-recurring-invoices',
            component: () => import('@modules/Accounting/Resources/js/pages/reports/RecurringInvoices.vue')
        },
        {
            path: 'reports/sales-by-customer',
            name: 'report-sales-by-customer',
            component: () => import('@modules/Accounting/Resources/js/pages/reports/SalesByCustomer.vue')
        },
        {
            path: 'reports/sales-by-product',
            name: 'report-sales-by-product',
            component: () => import('@modules/Accounting/Resources/js/pages/reports/SalesByProduct.vue')
        },
        {
            path: 'reports/sales-by-representative',
            name: 'report-sales-by-representative',
            component: () => import('@modules/Accounting/Resources/js/pages/reports/SalesByRepresentative.vue')
        },
        {
            path: 'reports/purchases-by-supplier',
            name: 'report-purchases-by-supplier',
            component: () => import('@modules/Accounting/Resources/js/pages/reports/PurchasesBySupplier.vue')
        },
        {
            path: 'reports/purchases-by-product',
            name: 'report-purchases-by-product',
            component: () => import('@modules/Accounting/Resources/js/pages/reports/PurchasesByProduct.vue')
        },
        // Customer Payments
        ...paymentRoutes('customer-payments', 'customer', {
            list: 'customer-payments-list',
            preview: 'customer-payment-preview',
            overview: 'customer-payment-overview',
            new: 'customer-payment-new',
            edit: 'customer-payment-edit'
        }),
        // Supplier Payments
        ...paymentRoutes('supplier-payments', 'supplier', {
            list: 'supplier-payments-list',
            preview: 'supplier-payment-preview',
            overview: 'supplier-payment-overview',
            new: 'supplier-payment-new',
            edit: 'supplier-payment-edit'
        }),
        // Document Templates
        {
            path: 'document-templates',
            name: 'document-template-list',
            component: () => import('@modules/Accounting/Resources/js/pages/documents/Templates.vue')
        },
        {
            path: 'document-templates/new',
            name: 'document-template-new',
            component: () => import('@modules/Accounting/Resources/js/pages/documents/TemplateForm.vue')
        },
        {
            path: 'document-templates/:id(\\d+)/edit',
            name: 'document-template-edit',
            component: () => import('@modules/Accounting/Resources/js/pages/documents/TemplateForm.vue'),
            props: route => ({ id: route.params.id })
        }
    ],
    logRoutes: [
        {
            // A tab of settings-logs rather than a child of the accounting settings screen:
            // this is a log to read, not a setting to change.
            path: 'ledger-trail',
            name: 'settings-ledger-trail',
            component: () => import('@modules/Accounting/Resources/js/pages/settings/LedgerTrail.vue')
        }
    ],
    logTabs: () => [
        {
            // The ledger's own trail, not accounting's in general: the module gate
            // passes for anyone holding any `accounting.*`, so a quotes-only
            // salesperson would read every posting ever made without this.
            title: i18n.t('Ledger audit trail'),
            icon: 'book-open-variant',
            route: 'settings-ledger-trail',
            ability: 'accounting.ledger'
        }
    ],
    settingsRoutes: [
        {
            path: 'accounting',
            name: 'settings-accounting-app',
            component: () => import('@modules/Accounting/Resources/js/pages/settings/Accounting.vue'),
            redirect: { name: 'settings-taxes' },
            children: [
                {
                    path: 'taxes',
                    name: 'settings-taxes',
                    component: () => import('@modules/Accounting/Resources/js/pages/settings/taxes/List.vue'),
                    props: true
                },
                {
                    path: 'chart-of-accounts',
                    name: 'settings-accounts',
                    component: () => import('@modules/Accounting/Resources/js/pages/settings/accounts/TableList.vue'),
                    props: true
                },
                {
                    path: 'payment-methods',
                    name: 'settings-payment-methods',
                    component: () => import('@modules/Accounting/Resources/js/pages/settings/payment-methods/List.vue'),
                    props: true
                },
                {
                    path: 'settings',
                    name: 'settings-accounting',
                    component: () => import('@modules/Accounting/Resources/js/pages/settings/Settings.vue'),
                    props: true
                }
            ]
        },
        {
            path: 'sales',
            name: 'settings-sales',
            component: () => import('@modules/Accounting/Resources/js/pages/settings/SalesSettings.vue'),
            props: true
        }
    ],
    settings: () => [
        {
            title: i18n.t('Applications'),
            order: 10,
            items: [
                {
                    title: i18n.t('Accounting'),
                    icon: 'book-open-variant',
                    route: 'settings-accounting-app',
                    module: 'accounting',
                    permission: 'accounts',
                    position: 30
                },
                {
                    title: i18n.t('Sales'),
                    icon: 'tag-outline',
                    route: 'settings-sales',
                    module: 'accounting',
                    permission: 'accounts',
                    position: 20
                }
            ]
        }
    ],
    reports: () => [
        {
            title: i18n.t('Financial Reports'),
            items: [
                {
                    route: 'report-profit-and-loss',
                    icon: 'finance',
                    title: i18n.t('Profit and Loss'),
                    description: i18n.t('Revenue and expenses by account for a period')
                },
                {
                    route: 'report-trial-balance',
                    icon: 'scale-balance',
                    title: i18n.t('Trial Balance'),
                    description: i18n.t('All accounts with debit and credit totals')
                },
                {
                    route: 'report-balance-sheet',
                    icon: 'bank',
                    title: i18n.t('Balance Sheet'),
                    description: i18n.t('Assets, liabilities and equity snapshot')
                },
                {
                    route: 'report-general-ledger',
                    icon: 'book-open-variant',
                    title: i18n.t('General Ledger'),
                    description: i18n.t('Transaction detail for a specific account')
                }
            ]
        },
        {
            title: i18n.t('Receivables & Payables'),
            items: [
                {
                    route: 'report-receivable-aging',
                    icon: 'account-cash',
                    title: i18n.t('Receivable Aging'),
                    description: i18n.t('Outstanding customer balances by age')
                },
                {
                    route: 'report-payable-aging',
                    icon: 'cash-minus',
                    title: i18n.t('Payable Aging'),
                    description: i18n.t('Outstanding supplier balances by age')
                },
                {
                    route: 'report-recurring-invoices',
                    icon: 'refresh',
                    title: i18n.t('Recurring Invoices'),
                    description: i18n.t('Schedule and generation status of recurring invoices')
                }
            ]
        },
        {
            title: i18n.t('Sales'),
            items: [
                {
                    route: 'report-sales-by-customer',
                    icon: 'account-group',
                    title: i18n.t('Sales per Customer'),
                    description: i18n.t('Invoice totals grouped by customer for a period')
                },
                {
                    route: 'report-sales-by-product',
                    icon: 'package-variant-closed',
                    title: i18n.t('Sales per Product'),
                    description: i18n.t('Invoice line items grouped by product for a period')
                },
                {
                    route: 'report-sales-by-representative',
                    icon: 'account-tie',
                    title: i18n.t('Sales per Representative'),
                    description: i18n.t('Invoice totals grouped by sales representative')
                }
            ]
        },
        {
            title: i18n.t('Purchases'),
            items: [
                {
                    route: 'report-purchases-by-supplier',
                    icon: 'truck',
                    title: i18n.t('Purchases per Supplier'),
                    description: i18n.t('Bill totals grouped by supplier for a period')
                },
                {
                    route: 'report-purchases-by-product',
                    icon: 'package-variant-closed',
                    title: i18n.t('Purchases per Product'),
                    description: i18n.t('Bill line items grouped by product for a period')
                }
            ]
        }
    ],
    // Like the menu below, each widget names the ability of the endpoint it
    // reads. The module-wide `accounting` matches anyone holding any part of
    // it — a mechanic allowed to raise purchase orders would see the books.
    widgets: () => [
        {
            id: 'accounts-receivable',
            label: i18n.t('Accounts Receivable'),
            icon: 'cash-plus',
            component: () => import('@modules/Accounting/Resources/js/pages/widgets/AccountsReceivable.vue'),
            size: 'is-4',
            ability: 'accounting.sales.invoicing'
        },
        {
            id: 'accounts-payable',
            label: i18n.t('Accounts Payable'),
            icon: 'cash-minus',
            component: () => import('@modules/Accounting/Resources/js/pages/widgets/AccountsPayable.vue'),
            size: 'is-4',
            ability: 'accounting.purchases.invoicing'
        },
        {
            id: 'sales-purchases',
            label: i18n.t('Sales / Purchases'),
            icon: 'chart-line',
            component: () => import('@modules/Accounting/Resources/js/pages/widgets/SalesPurchases.vue'),
            size: 'is-12',
            ability: 'accounting.ledger'
        },
        {
            id: 'financial-summary',
            label: i18n.t('Financial Summary'),
            icon: 'finance',
            component: () => import('@modules/Accounting/Resources/js/pages/widgets/FinancialSummary.vue'),
            size: 'is-6',
            ability: 'accounting.ledger'
        },
        {
            id: 'cash-flow',
            label: i18n.t('Cash Flow'),
            icon: 'cash-fast',
            component: () => import('@modules/Accounting/Resources/js/pages/widgets/CashFlow.vue'),
            size: 'is-6',
            ability: 'accounting.ledger'
        }
    ],
    portalRoutes: [
        {
            path: 'invoices',
            name: 'portal-invoices',
            component: () => import('@modules/Accounting/Resources/js/portal/List.vue'),
            props: { type: 'invoices' }
        },
        {
            path: 'invoices/:id(\\d+)',
            name: 'portal-invoice-detail',
            component: () => import('@modules/Accounting/Resources/js/portal/Preview.vue'),
            props: route => ({ type: 'invoices', id: route.params.id })
        },
        {
            path: 'quotes',
            name: 'portal-quotes',
            component: () => import('@modules/Accounting/Resources/js/portal/List.vue'),
            props: { type: 'quotes' }
        },
        {
            path: 'quotes/:id(\\d+)',
            name: 'portal-quote-detail',
            component: () => import('@modules/Accounting/Resources/js/portal/Preview.vue'),
            props: route => ({ type: 'quotes', id: route.params.id })
        },
        {
            path: 'orders',
            name: 'portal-orders',
            component: () => import('@modules/Accounting/Resources/js/portal/List.vue'),
            props: { type: 'orders' }
        },
        {
            path: 'orders/:id(\\d+)',
            name: 'portal-order-detail',
            component: () => import('@modules/Accounting/Resources/js/portal/Preview.vue'),
            props: route => ({ type: 'orders', id: route.params.id })
        },
        {
            path: 'bills',
            name: 'portal-bills',
            component: () => import('@modules/Accounting/Resources/js/portal/List.vue'),
            props: { type: 'bills' }
        },
        {
            path: 'bills/:id(\\d+)',
            name: 'portal-bill-detail',
            component: () => import('@modules/Accounting/Resources/js/portal/Preview.vue'),
            props: route => ({ type: 'bills', id: route.params.id })
        },
        {
            path: 'recurring-invoices',
            name: 'portal-recurring-invoices',
            component: () => import('@modules/Accounting/Resources/js/portal/List.vue'),
            props: { type: 'recurring-invoices' }
        },
        {
            path: 'recurring-invoices/:id(\\d+)',
            name: 'portal-recurring-invoice-detail',
            component: () => import('@modules/Accounting/Resources/js/portal/Preview.vue'),
            props: route => ({ type: 'recurring-invoices', id: route.params.id })
        },
        {
            path: 'purchase-orders',
            name: 'portal-purchase-orders',
            component: () => import('@modules/Accounting/Resources/js/portal/List.vue'),
            props: { type: 'purchase-orders' }
        },
        {
            path: 'purchase-orders/:id(\\d+)',
            name: 'portal-purchase-order-detail',
            component: () => import('@modules/Accounting/Resources/js/portal/Preview.vue'),
            props: route => ({ type: 'purchase-orders', id: route.params.id })
        }
    ],
    // Each entry names the ability its own screens need, matching the gate on
    // its routes. The module-wide ability is too coarse here: an organization
    // can hold Accounting for quoting alone, and the menu has to say so rather
    // than offer invoicing the server will refuse. Customers is absent because
    // it is Contacts' entry now, in this same section: it is held whether or not
    // any of this module was bought.
    nav: () => [
        {
            section: 'sales',
            items: [
                {
                    title: i18n.t('Quotes'),
                    icon: 'file-document-outline',
                    to: { name: 'sales-quotes' },
                    ability: 'accounting.sales.quotes',
                    position: 2
                },
                {
                    title: i18n.t('Orders'),
                    icon: 'cart-outline',
                    to: { name: 'sales-orders' },
                    ability: 'accounting.sales.orders',
                    position: 3
                },
                {
                    title: i18n.t('Invoices'),
                    icon: 'file-document',
                    to: { name: 'sales-invoices' },
                    ability: 'accounting.sales.invoicing',
                    position: 4
                },
                {
                    title: i18n.t('Credit Notes'),
                    icon: 'file-undo-outline',
                    to: { name: 'sales-credit-notes' },
                    ability: 'accounting.sales.invoicing',
                    position: 8
                },
                {
                    title: i18n.t('Payments'),
                    icon: 'cash-plus',
                    to: { name: 'customer-payments-list' },
                    ability: 'accounting.sales.invoicing',
                    position: 7
                }
            ]
        },
        {
            section: 'purchases',
            items: [
                {
                    title: i18n.t('Suppliers'),
                    icon: 'store-outline',
                    to: { name: 'suppliers-list' },
                    ability: 'accounting.purchases.suppliers',
                    position: 1
                },
                {
                    title: i18n.t('Orders'),
                    icon: 'basket-outline',
                    to: { name: 'purchases-orders' },
                    ability: 'accounting.purchases.orders',
                    position: 2
                },
                {
                    title: i18n.t('Bills'),
                    icon: 'receipt',
                    to: { name: 'purchases-invoices' },
                    ability: 'accounting.purchases.invoicing',
                    position: 3
                },
                {
                    title: i18n.t('Credit Notes'),
                    icon: 'file-undo-outline',
                    to: { name: 'purchases-credit-notes' },
                    ability: 'accounting.purchases.invoicing',
                    position: 8
                },
                {
                    title: i18n.t('Payments'),
                    icon: 'cash-minus',
                    to: { name: 'supplier-payments-list' },
                    ability: 'accounting.purchases.invoicing',
                    position: 6
                },
                {
                    title: i18n.t('Expenses'),
                    icon: 'credit-card-outline',
                    to: { name: 'expenses-list' },
                    ability: 'accounting.purchases.invoicing',
                    position: 7
                }
            ]
        },
        {
            section: 'accounting',
            items: [
                {
                    title: i18n.t('Banking'),
                    icon: 'bank',
                    to: { name: 'banking-accounts' },
                    ability: 'accounting.ledger'
                },
                {
                    title: i18n.t('Journal Entries'),
                    icon: 'notebook-outline',
                    to: { name: 'journal-entries-list' },
                    ability: 'accounting.ledger'
                },
                // Spans sales and purchases, so it sits with the entries that
                // cover both rather than under either one.
                {
                    title: i18n.t('Recurrences'),
                    icon: 'autorenew',
                    to: { name: 'recurring-schedules' },
                    ability: 'accounting.recurrences'
                },
                {
                    title: i18n.t('Tax Reports'),
                    icon: 'file-percent-outline',
                    to: { name: 'tax-reports-list' },
                    // The entry is offered here, but the screen behind it is the
                    // Reports module's: an organization holding Accounting
                    // without Reports was shown a menu item that answered 403.
                    module: 'reports',
                    ability: 'accounting.ledger'
                }
            ]
        }
    ]
};
